import React from 'react';

import CSSModules from 'react-css-modules';
import ReactTooltip from 'react-tooltip';

import {EMAIL_BOTH, TEL_BOTH} from '@edume/bento/auth';
import {EXPIRED_TOKEN} from '@edume/bento/errorCodes';

import Link from '../Link';
import OtpEntryInput from '../OtpEntryInput';
import Heading from '../TextComponents/Heading';
import Text from '../TextComponents/Text';

import styles from './styles.module.scss';

const OTP_LENGTH = 6;

type ContentShape = {
  welcome?: string;
  intro: string;
  instruction: string;
  error: {
    expiredToken: string;
    generic: string;
  };
  inputTitle: string;
  additionalCopy: string;
  editContact: string;
  retry: string;
  alternative: string;
  or: string;
  tooltip: string;
};

export type OtpEntryFormProps = {
  contactType: 'both' | 'email' | 'tel' | 'both-tel';
  onChange: (otp: string) => void;
  onRetry: () => void;
  onEditDetails?: () => void;
  onUseDifferentContactMethod?: () => void;
  otpError: string;
  tooltipRef: React.RefObject<HTMLDivElement>;
  content: ContentShape;
  otp: string;
  contactDetails: {email?: string; phone?: string};
};

// eslint-disable-next-line complexity
const OtpEntryForm = ({
  contactType,
  contactDetails,
  onChange,
  otpError,
  onRetry,
  onEditDetails,
  onUseDifferentContactMethod,
  tooltipRef,
  content,
  otp,
}: OtpEntryFormProps): JSX.Element => {
  const {phone, email} = contactDetails;
  const {
    welcome,
    intro,
    instruction,
    inputTitle,
    additionalCopy,
    error,
    editContact,
    retry,
    alternative,
    or,
    tooltip,
  } = content;

  const getError = () => {
    if (!otpError) {
      return null;
    }

    const errorMessage =
      otpError === EXPIRED_TOKEN ? error.expiredToken : error.generic;
    return (
      <Text
        size='m'
        colour='red'
        weight='medium'
        dataAutomation='error-message'
      >
        {errorMessage}
      </Text>
    );
  };

  return (
    <form styleName='form' noValidate>
      {welcome && <h2>{welcome}</h2>}
      <div styleName='intro'>
        <Text size='m'>{intro}</Text>
      </div>
      {instruction && (
        <div styleName='intro'>
          <Text size='m'>{instruction}</Text>
        </div>
      )}

      {onEditDetails && (email || phone) && (
        <>
          <Text size='m' weight='medium'>
            {email || phone}
          </Text>
          <Link linkSize='snail' onClick={onEditDetails} text={editContact} />
        </>
      )}

      <div styleName='input-title'>
        <Heading as='h2' size='snail' weight='medium'>
          {inputTitle}
        </Heading>
      </div>

      <OtpEntryInput
        value={otp}
        valueLength={OTP_LENGTH}
        onChange={onChange}
        hasError={!!otpError}
      />

      <div styleName='error'>{getError()}</div>

      <div styleName='additional-copy'>
        <Text size='m'>{additionalCopy}</Text>
      </div>

      <div styleName='links-wrapper'>
        <div data-for='retry' data-tip='retry' ref={tooltipRef}>
          <Link linkSize='snail' text={retry} onClick={onRetry} />
        </div>

        <ReactTooltip
          id='retry'
          place='right'
          event='click'
          isCapture={true}
          html={true}
          afterShow={() => setTimeout(ReactTooltip.hide, 2000)}
        >
          {tooltip}
        </ReactTooltip>

        {onUseDifferentContactMethod &&
          [EMAIL_BOTH, TEL_BOTH].includes(contactType) && (
            <>
              <Text size='m' inline>
                {or}
              </Text>
              <Link
                linkSize='snail'
                text={alternative}
                onClick={onUseDifferentContactMethod}
              />
            </>
          )}
      </div>
    </form>
  );
};

export default CSSModules(OtpEntryForm, styles, {allowMultiple: true});
